import { NavigateFunction } from 'react-router';
import { PageConfigType } from './Page';
import { PlayingAssetType } from './Player';

export const FAVORITES_CUSTOM_ATTR_ID = 'MY_ELISA';

export type DetailRoutesLanguages = 'en' | 'et' | 'ru';
export type RouteMap<T extends string> = Record<T, string>;
export type LanguageRouteMap<T extends string> = Record<DetailRoutesLanguages, Partial<RouteMap<T>>>;

export type DetailRoutesKeyType = 'VOD_SERIES' | 'TV_SERIES' | 'VOD_MOVIE' | 'MOVIE' | 'PROGRAM' | 'PROGRAM_SERIES' | 'RECORDING';
export type CollectionRouteKeyType = 'NOW' | 'RECORDING' | 'VOD_PURCHASES' | 'CONTINUE_WATCHING' | 'WATCHLIST_MOVIES' | 'WATCHLIST_SERIES';
export type FavoriteRouteKeyType = 'FAVORITE';

export type DetailRoutesType = RouteMap<DetailRoutesKeyType>;
export type CollectionRoutesType = RouteMap<CollectionRouteKeyType>;
export type FavoriteRouteType = RouteMap<FavoriteRouteKeyType>;

export type DetailRoutesInterface = LanguageRouteMap<DetailRoutesKeyType>;
export type CollectionRoutesInterface = LanguageRouteMap<CollectionRouteKeyType>;
export type FavoriteRouteInterface = LanguageRouteMap<FavoriteRouteKeyType>;

// Route definitions
export const DetailRoutes: DetailRoutesInterface = {
    en: {
        VOD_SERIES: `/vod-series/:id`,
        TV_SERIES: '/program-series/:id',
        MOVIE: '/movie/:id',
        PROGRAM: '/program/:id',
        RECORDING: '/recording/:id',
    },
    et: {
        VOD_SERIES: `/seriaalid/:id`,
        TV_SERIES: '/seriaalid-telekanalitelt/:id',
        MOVIE: '/filmid/:id',
        PROGRAM: '/saated/:id',
        RECORDING: '/salvestused/:id',
    },
    ru: {
        VOD_SERIES: `/seriaalid/:id`,
        TV_SERIES: '/seriaalid-telekanalitelt/:id',
        MOVIE: '/filmid/:id',
        PROGRAM: '/saated/:id',
        RECORDING: '/salvestused/:id',
    },
};

export const DetailRouteDomain: DetailRoutesInterface = {
    en: {
        VOD_SERIES: 'vod-series',
        TV_SERIES: 'program-series',
        VOD_MOVIE: 'movie',
        PROGRAM: 'program',
        RECORDING: 'recording',
    },
    et: {
        VOD_SERIES: 'seriaalid',
        TV_SERIES: 'seriaalid-telekanalitelt',
        VOD_MOVIE: 'filmid',
        PROGRAM: 'saated',
        RECORDING: 'salvestused',
    },
    ru: {
        VOD_SERIES: 'seriaalid',
        TV_SERIES: 'seriaalid-telekanalitelt',
        VOD_MOVIE: 'filmid',
        PROGRAM: 'saated',
        RECORDING: 'salvestused',
    },
};

export const CollectionRoute: CollectionRoutesInterface = {
    en: {
        NOW: 'collection/now-on-tv',
        RECORDING: 'collection/recordings',
        VOD_PURCHASES: 'collection/vod-purchases',
        CONTINUE_WATCHING: 'collection/continue-watching',
        WATCHLIST_MOVIES: 'collection/my-movies',
        WATCHLIST_SERIES: 'collection/my-series',
    },
    et: {
        NOW: 'collection/hetkel-eetris',
        RECORDING: 'collection/salvestused',
        VOD_PURCHASES: 'collection/vod-ostud',
        CONTINUE_WATCHING: 'collection/jatka-vaatamist',
        WATCHLIST_MOVIES: 'collection/minu-filmid',
        WATCHLIST_SERIES: 'collection/minu-sarjad',
    },
    ru: {
        NOW: 'collection/now-on-tv',
        RECORDING: 'collection/recordings',
        VOD_PURCHASES: 'collection/vod-purchases',
        CONTINUE_WATCHING: 'collection/continue-watching',
        WATCHLIST_MOVIES: 'collection/my-movies',
        WATCHLIST_SERIES: 'collection/my-series',
    },
};

export const FavoriteRoute: FavoriteRouteInterface = {
    en: { FAVORITE: '/favorite' },
    et: { FAVORITE: '/favorite' },
    ru: { FAVORITE: '/favorite' },
};

export enum PlayerRoutes {
    LIVE = '/play/live/:channelId',
    CATCHUP = '/play/catchup/:broadcastId',
    VOD = '/play/vod/:assetId',
    TRAILER = '/play/trailer/:assetId',
    RECORDING = '/play/recording/:recordingId',
}

export const PlayerRouteToAssetType: Record<keyof typeof PlayerRoutes, PlayingAssetType> = {
    LIVE: PlayingAssetType.LIVE,
    CATCHUP: PlayingAssetType.CATCHUP,
    VOD: PlayingAssetType.VOD,
    TRAILER: PlayingAssetType.TRAILER,
    RECORDING: PlayingAssetType.RECORDING,
};

export enum StaticPageRoutes {
    COLLECTION = '/collection/:pagePath/:collectionId?',
    EXPANDABLE_PAGE = '/:pageSlug/:pageId',
    SETTINGS = '/settings',
    ABOUT = '/settings/about',
    RECORDINGS_SETTINGS = '/settings/recordings',
    AUTOPLAY = '/settings/autoplay',
    LANGUAGE = '/settings/language',
    MENU_LANGUAGE = '/settings/language/menu',
    AUDIO_LANGUAGE = '/settings/language/audio',
    SUBTITLES_LANGUAGE = '/settings/language/subtitles',
    DEVICE_MANAGER = '/settings/device-manager',
    ACCOUNT = '/settings/account',
    SUBSCRIPTION = '/settings/subscription',
    PIN_PROTECTION = '/settings/pin',
    ACCESSIBILITY = '/settings/accessibility',
    TAKEOVER = '/settings/takeover',
    ACCESSIBILITY_SUBTITLES = '/settings/accessibility/subtitles',
    SEARCH = '/search',
    DEV = '/dev-screen',
    EPG = 'live-tv',
    EPG_ET = 'kava',
    EPG_RU = 'телепрограмма',
    RECORDINGS = '/recordings',
    RECORDINGS_ET = '/salvestused',
    RECORDINGS_RU = '/recordings',
    VOD_PURCHASES = '/vod-purchases',
    VOD_PURCHASES_ET = '/vod-ostud',
    VOD_PURCHASES_RU = '/vod-purchases',
    FAVORITES = '/my-elisa',
    ACCESSIBILITY_NOTICE = '/accessibility-notice',
}

export type Route = {
    path: string;
    component: any;
    childRoutes: Route[];
    type: string;
    pageConfig: PageConfigType;
};

export type PagePropTypes = {
    navigate: NavigateFunction;
    match: {
        params: any;
        isExact: boolean;
        path: string;
        url: string;
    };
};
